import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFetching } from '@tanstack/react-query';

import { Button } from '@unified-checkout/design-system';

import type { INotificationProps } from '@app/dashboard/types/innerLayout/notifications';
import { NOTIFICATION_FLAGS } from '@app/dashboard/constants/settings/notifications';

import { checkIfSaveChangesDisabled } from '../utils.js';

import ToggleConfirmation from './ToggleModalConfirmation';

import styles from './Footer.module.scss';

const Footer = ({
  merchantFlags,
  updatedFlags,
  setUpdatedFlags,
}: INotificationProps) => {
  const { t } = useTranslation();
  const isFetching = useIsFetching();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isSaveChangesDisabled = useMemo(() => {
    if (!merchantFlags || !updatedFlags) return true;

    return checkIfSaveChangesDisabled({ merchantFlags, updatedFlags });
  }, [merchantFlags, updatedFlags]);

  const handleCancelChanges = () => {
    const updated = Object.values(NOTIFICATION_FLAGS).reduce((acc, flag) => {
      const flagValue = merchantFlags[flag as keyof typeof merchantFlags];

      acc[flag as keyof typeof merchantFlags] = flagValue;

      return acc;
    }, {} as typeof merchantFlags);

    setUpdatedFlags(updated);
  };

  const handleSaveChanges = () => {
    setIsModalOpen(true);
  };

  return (
    <div className={styles['footer']}>
      <ToggleConfirmation
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        updatedFlags={updatedFlags}
      />

      <Button
        text={t('innerLayout.notificationsPage.actions.cancel')}
        variant="outlined"
        color="secondary"
        size="md"
        onClickHandler={handleCancelChanges}
        disabled={isSaveChangesDisabled || !!isFetching}
      />
      <Button
        text={t('innerLayout.notificationsPage.actions.saveChanges')}
        variant="contained"
        color="primary"
        size="md"
        onClickHandler={handleSaveChanges}
        disabled={isSaveChangesDisabled || !!isFetching}
      />
    </div>
  );
};

export default Footer;
